import type { LearningContentItem } from '@services/firebase/learning'

export const LEGACY_SELF_STUDY_TEMPLATE_IDS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '10',
  '11',
  '12',
  '13',
] as const

export type LegacySelfStudyTemplateId =
  (typeof LEGACY_SELF_STUDY_TEMPLATE_IDS)[number]

type LegacySelfStudyTemplateFamily =
  | 'conversation'
  | 'examples'
  | 'fill-in'
  | 'match'
  | 'quiz'
  | 'sentence-rebuild'
  | 'speech'
  | 'vocabulary'
  | 'write'

interface LegacySelfStudyTemplateContract {
  readonly family: LegacySelfStudyTemplateFamily
  readonly label: string
  readonly mode: 'source-owned' | 'compatibility-shim'
  readonly sourceOwner: string
}

// Keep every published legacy ID declared with its controlling local legacy
// source owner. QA must not accept a published template as an adapter-only
// surface when a source owner exists here.
export const LEGACY_SELF_STUDY_TEMPLATE_CONTRACT: Record<
  LegacySelfStudyTemplateId,
  LegacySelfStudyTemplateContract
> = {
  '1': {
    family: 'quiz',
    label: 'Choose the answer',
    mode: 'source-owned',
    sourceOwner: 'Template1.js -> BoxQuestion.js -> Quiz.js',
  },
  '2': {
    family: 'write',
    label: 'Write the answer',
    mode: 'source-owned',
    sourceOwner: 'Template2.js -> BoxQuestion.js -> Write.js',
  },
  '3': {
    family: 'sentence-rebuild',
    label: 'Arrange the sentence',
    mode: 'source-owned',
    sourceOwner: 'Template3.js -> BoxQuestion.js -> ShuffleWordsOfSentence.js',
  },
  '4': {
    family: 'sentence-rebuild',
    label: 'Arrange the word',
    mode: 'source-owned',
    sourceOwner: 'Template4.js -> BoxQuestion.js -> ShuffleLettersOfWords.js',
  },
  '5': {
    family: 'fill-in',
    label: 'Complete the sentence',
    mode: 'source-owned',
    sourceOwner: 'Template5.js -> BoxQuestion.js -> CompleteSentence.js',
  },
  '6': {
    family: 'match',
    label: 'Match the answers',
    mode: 'source-owned',
    sourceOwner: 'Template6.js -> BoxQuestion.js -> MatchColumn.js',
  },
  '7': {
    family: 'speech',
    label: 'Speak the answer',
    mode: 'source-owned',
    sourceOwner: 'Template7.js -> BoxQuestion.js -> InputSpeechRecognition.js',
  },
  '10': {
    family: 'examples',
    label: 'Practice with examples',
    mode: 'source-owned',
    sourceOwner: 'Template10.js -> BoxQuestion.js -> BoxExamples.js',
  },
  '11': {
    family: 'conversation',
    label: 'Practice the conversation',
    mode: 'source-owned',
    sourceOwner: 'Template11.js -> Conversation.js / ConversationPractice.js / ConversationFillin.js',
  },
  '12': {
    family: 'vocabulary',
    label: 'Vocabulary drill',
    mode: 'source-owned',
    sourceOwner: 'Template12.js -> BoxVocabulary.js',
  },
  '13': {
    family: 'speech',
    label: 'Speed-to-text practice',
    mode: 'source-owned',
    sourceOwner: 'Template13.js -> Template13Details.js -> BoxSpeedToText.js',
  },
}

export function resolveLegacySelfStudyTemplate(
  item: Pick<LearningContentItem, 'template' | 'type'>,
): LegacySelfStudyTemplateId | null {
  const template = String(item.template ?? '').trim()

  if (LEGACY_SELF_STUDY_TEMPLATE_IDS.includes(template as LegacySelfStudyTemplateId)) {
    return template as LegacySelfStudyTemplateId
  }

  const normalizedTemplate = template
    .toLowerCase()
    .replace(/[_\-\s]+/g, ' ')
    .trim()

  const normalizedType = String(item.type ?? '')
    .toLowerCase()
    .replace(/[_\-\s]+/g, ' ')
    .trim()

  const sourceAlias = normalizedTemplate || normalizedType

  if (
    sourceAlias === 'template 13' ||
    sourceAlias === 'speed to text' ||
    sourceAlias === 'speech to text' ||
    sourceAlias === 'speaking practice' ||
    sourceAlias === 'pronouncing practice'
  ) {
    return '13'
  }

  if (
    sourceAlias === 'template 1' ||
    sourceAlias === 'quiz' ||
    sourceAlias === 'multiple choice' ||
    sourceAlias === 'choose answer' ||
    sourceAlias === 'choose the answer'
  ) {
    return '1'
  }

  return null
}

export function getLegacySelfStudyTemplateContract(
  template: LegacySelfStudyTemplateId | null,
) {
  return template ? LEGACY_SELF_STUDY_TEMPLATE_CONTRACT[template] : null
}
